import React, { useLayoutEffect, useRef } from 'react';
import { NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { addColumn, commitResize, deleteColumn, MIN_FRACTION, moveColumn } from './commands';

// React NodeView for one column. NB: ReactNodeViewRenderer wraps the component in
// an extra `div.react-renderer` which is the ACTUAL ProseMirror node DOM (the
// flex item, a direct child of the .rc-columns row); our NodeViewWrapper
// (.rc-column) lives inside it. So width + resize must act on that host element,
// not on .rc-column. We reach it via wrapperRef.current.parentElement.
export const ColumnView: React.FC<NodeViewProps> = ({ node, editor, getPos }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const width = typeof node.attrs.width === 'number' ? node.attrs.width : null;

  // Apply the column's width to the flex item (the .react-renderer host).
  // alignSelf:flex-start keeps the column sized to its own content (no stretch to
  // the tallest sibling), set here in JS so it holds even if a CSS hot-reload lags.
  useLayoutEffect(() => {
    const host = wrapperRef.current?.parentElement;
    if (!host) return;
    host.style.flexGrow = width ? '0' : '1';
    host.style.flexShrink = '1';
    host.style.flexBasis = width ? `${width * 100}%` : '0%';
    host.style.alignSelf = 'flex-start';
  }, [width]);

  const swallow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const posOf = (): number | null => {
    const p = getPos?.();
    return typeof p === 'number' ? p : null;
  };
  const act = (fn: (pos: number) => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = posOf();
    if (pos != null) fn(pos);
  };

  const onResizeDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const host = wrapperRef.current?.parentElement as HTMLElement | null; // flex item
    const rightHost = host?.nextElementSibling as HTMLElement | null;
    const rowEl = host?.parentElement as HTMLElement | null; // .rc-columns
    const pos = posOf();
    if (!host || !rightHost || !rowEl || pos == null) return;

    const leftPos = pos;
    const rightPos = pos + node.nodeSize;
    const leftNode = editor.state.doc.nodeAt(leftPos);
    const rightNode = editor.state.doc.nodeAt(rightPos);
    if (!leftNode || !rightNode || rightNode.type.name !== 'column') return;

    const totalPx = rowEl.getBoundingClientRect().width;
    if (totalPx <= 0) return;
    const lw0 = typeof leftNode.attrs.width === 'number' ? leftNode.attrs.width : host.getBoundingClientRect().width / totalPx;
    const rw0 = typeof rightNode.attrs.width === 'number' ? rightNode.attrs.width : rightHost.getBoundingClientRect().width / totalPx;
    const pair = lw0 + rw0;
    const startX = e.clientX;
    let lw = lw0;
    let rw = rw0;

    try {
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }

    // Live DOM on the flex items (zoom-invariant ratio); one commit on pointerup.
    const move = (ev: PointerEvent) => {
      const delta = (ev.clientX - startX) / totalPx;
      lw = Math.min(pair - MIN_FRACTION, Math.max(MIN_FRACTION, lw0 + delta));
      rw = pair - lw;
      host.style.flexGrow = '0';
      host.style.flexBasis = `${lw * 100}%`;
      rightHost.style.flexGrow = '0';
      rightHost.style.flexBasis = `${rw * 100}%`;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      commitResize(editor, leftPos, rightPos, Math.round(lw * 1000) / 1000, Math.round(rw * 1000) / 1000);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <NodeViewWrapper ref={wrapperRef} as="div" className="rc-column" data-type="column">
      <div className="rc-col-controls" contentEditable={false}>
        <button className="rc-col-btn" title="Move column left" onMouseDown={swallow} onClick={act((p) => moveColumn(editor, p, -1))}>
          ◀
        </button>
        <button className="rc-col-btn" title="Move column right" onMouseDown={swallow} onClick={act((p) => moveColumn(editor, p, 1))}>
          ▶
        </button>
        <button className="rc-col-btn" title="Add column" onMouseDown={swallow} onClick={act((p) => addColumn(editor, p))}>
          ＋
        </button>
        <button className="rc-col-btn" title="Delete column" onMouseDown={swallow} onClick={act((p) => deleteColumn(editor, p))}>
          ✕
        </button>
      </div>

      <NodeViewContent className="rc-col-content" />

      <div className="rc-col-resize" contentEditable={false} onMouseDown={swallow} onPointerDown={onResizeDown} title="Drag to resize" />
    </NodeViewWrapper>
  );
};
